import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  SlidersHorizontal as Filters,
  Pencil,
  MoreVertical,
  Eye,
  Trash2,
  Phone,
  MapPin,
  User,
  X,
  Loader2
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './Customers.module.css';
import CustomerDrawer from './CustomerDrawer';
import Pagination from '../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const Customers = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    created_by: '',
    mobile: ''
  });
  const [filterData, setFilterData] = useState({
    users: [],
    customers: []
  });

  // Permission guards
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create customers');
  const canEdit   = isAdmin || userPermissions.includes('edit customers');
  const canDelete = isAdmin || userPermissions.includes('delete customers');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchTerm !== debouncedSearch) {
        setPagination(prev => ({ ...prev, current_page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (id, e) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(id);
    }
  };

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleAddClick = () => {
    setEditingCustomer(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (customer) => {
    setEditingCustomer(customer);
    setIsDrawerOpen(true);
  };

  const handleDelete = async (customer) => {
    setOpenMenuIndex(null);
    
    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Customer</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Customer?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete <strong>${customer.customer_name}</strong>?<br/>
          This action cannot be undone.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/customers/${customer.id}`);
          if (response.data.success) {
            toast.success('Customer deleted successfully');
            fetchCustomers();
          }
        } catch (error) {
          console.error('Error deleting customer:', error);
          toast.error(error.response?.data?.message || 'Failed to delete customer');
        }
      }
    });
  };

  const handleRefresh = () => {
    fetchCustomers();
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const [userRes, customerRes] = await Promise.all([
        api.get('/users?per_page=100'),
        api.get('/customers-dropdown')
      ]);
      
      setFilterData({
        users: userRes.data.data.data || [],
        customers: customerRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [pagination.current_page, pagination.per_page, debouncedSearch, filters]);

  const fetchCustomers = async () => {
    showLoader();
    try {
      let url = `/customers?page=${pagination.current_page}&per_page=${pagination.per_page}&search=${encodeURIComponent(debouncedSearch)}`;
      
      if (filters.created_by) {
        url += `&created_by=${filters.created_by}`;
      }
      
      if (filters.mobile) {
        url += `&mobile=${encodeURIComponent(filters.mobile)}`;
      }

      const response = await api.get(url);
      if (response.data.success) {
        setCustomers(response.data.data.data);
        const { current_page, last_page, total, per_page } = response.data.data;
        setPagination({ current_page, last_page, total, per_page });
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      hideLoader();
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      created_by: '',
      mobile: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const getInitials = (name) => {
    if (!name) return 'CU';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ['#fee2e2', '#fef3c7', '#dcfce7', '#d1fae5', '#e0f2fe', '#f3e8ff', '#fce7f3'];
    if (!name) return colors[0];
    const charCodeSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return colors[charCodeSum % colors.length];
  };

  const getAvatarTextColor = (bgColor) => {
    return bgColor.replace('f', '9').replace('e', '7').replace('d', '5');
  };

  return (
    <div className={`${styles['customers-layout']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Customers</h1>
              <p>Manage your client directory and contact details.</p>
            </div>
            {canCreate && (
              <button className={styles['add-btn']} onClick={handleAddClick}>
                <Plus size={20} />
                <span>Add Customer</span>
              </button>
            )}
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search customers..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }))}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button 
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.created_by !== '' || filters.mobile !== '') && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Criteria</h3>
                  <p>Refine your customer directory with specific parameters.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Created By</label>
                  <CustomSelect
                    value={filters.created_by ? { 
                      value: filters.created_by, 
                      label: filterData.users.find(u => u.id === parseInt(filters.created_by))?.name 
                    } : null}
                    onChange={(opt) => handleFilterChange('created_by', opt ? opt.value : '')}
                    options={filterData.users.map(u => ({ value: u.id, label: u.name }))}
                    placeholder="All Users"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Mobile Number</label>
                  <CustomSelect
                    value={filters.mobile ? { 
                      value: filters.mobile, 
                      label: filterData.customers.find(c => c.mobile === filters.mobile)?.mobile 
                    } : null}
                    onChange={(opt) => handleFilterChange('mobile', opt ? opt.value : '')}
                    options={[...new Set(filterData.customers.map(c => c.mobile).filter(Boolean))].map(mobile => ({
                      value: mobile,
                      label: mobile
                    }))}
                    placeholder="Select Mobile"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            <div className={styles['table-wrapper']}>
              <table className={styles['customer-table']}>
                <thead>
                  <tr>
                    <th>CUSTOMER NAME</th>
                    <th>MOBILE</th>
                    <th>ADDRESS</th>
                    <th>CREATED BY</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length > 0 ? (
                    customers.map((customer) => (
                      <tr key={customer.id}>
                        <td className={styles['name-cell']}>
                          <div className={styles['user-info']}>
                            <div
                              className={styles['avatar']}
                              style={{
                                backgroundColor: getAvatarColor(customer.customer_name),
                                color: getAvatarTextColor(getAvatarColor(customer.customer_name))
                              }}
                            >
                              {getInitials(customer.customer_name)}
                            </div>
                            <span className={styles['name-text']}>{customer.customer_name}</span>
                          </div>
                        </td>
                        <td>{customer.mobile || 'N/A'}</td>
                        <td>{customer.address || 'N/A'}</td>
                        <td>{customer.creator?.name || 'N/A'}</td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === customer.id ? styles['active'] : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleActionMenu(customer.id, e);
                              }}
                            >
                              <MoreVertical size={18} />
                            </button>

                            {openMenuIndex === customer.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{ top: menuPosition.top, left: menuPosition.left }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button className={styles['dropdown-item']} onClick={() => navigate(`/customers/view/${customer.id}`)}>
                                  <Eye size={16} /> View
                                </button>
                                {canEdit && (
                                  <button className={styles['dropdown-item']} onClick={() => {
                                    handleEditClick(customer);
                                    setOpenMenuIndex(null);
                                  }}>
                                    <Pencil size={16} /> Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <>
                                    <div className={styles['dropdown-divider']}></div>
                                    <button 
                                      className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                                      onClick={() => handleDelete(customer)}
                                    >
                                      <Trash2 size={16} /> Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    !loading && <NoData message="No customers found." colSpan={5} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles['customer-cards']}>
              {customers.length > 0 ? (
                customers.map((customer) => (
                  <div key={customer.id} className={styles['customer-card']}>
                    <div className={styles['card-main']}>
                      <div
                        className={styles['card-avatar']}
                        style={{
                          backgroundColor: getAvatarColor(customer.customer_name),
                          color: getAvatarTextColor(getAvatarColor(customer.customer_name))
                        }}
                      >
                        {getInitials(customer.customer_name)}
                      </div>
                      <div className={styles['card-info']}>
                        <span className={styles['card-name']}>{customer.customer_name}</span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === customer.id ? styles['active'] : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(customer.id, e);
                          }}
                        >
                          <MoreVertical size={18} />
                        </button>

                        {openMenuIndex === customer.id && (
                          <div
                            className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                            style={{ top: menuPosition.top, left: menuPosition.left }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button className={styles['dropdown-item']} onClick={() => navigate(`/customers/view/${customer.id}`)}>
                              <Eye size={16} /> View
                            </button>
                            {canEdit && (
                              <button className={styles['dropdown-item']} onClick={() => {
                                handleEditClick(customer);
                                setOpenMenuIndex(null);
                              }}>
                                <Pencil size={16} /> Edit
                              </button>
                            )}
                            {canDelete && (
                              <>
                                <div className={styles['dropdown-divider']}></div>
                                <button 
                                  className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                                  onClick={() => handleDelete(customer)}
                                >
                                  <Trash2 size={16} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Mobile</span>
                        <span className={styles['detail-value']}>{customer.mobile || 'N/A'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Address</span>
                        <span className={styles['detail-value']}>{customer.address || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                !loading && <NoData message="No customers found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={pagination.current_page}
              totalItems={pagination.total}
              itemsPerPage={pagination.per_page}
              onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
            />
          </div>
        
          <Footer />
        </main>
      </div>

      <CustomerDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleRefresh}
        customer={editingCustomer}
      />
    </div>
  );
};

export default Customers;
